import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Select, 
  MenuItem, 
  Button, 
  Snackbar, 
  Alert, 
  CircularProgress,
  Container,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { colors, commonStyles, getButtonStyle } from '../theme/utils';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';

// Static class levels (should match backend ClassLevel enum)
const CLASS_LEVELS = [
  'pre_primary_1', 'pre_primary_2', 'grade_1', 'grade_2', 'grade_3', 'grade_4', 'grade_5', 'grade_6',
  'grade_7', 'grade_8', 'grade_9', 'grade_10', 'grade_11', 'grade_12'
];

const CLASS_LEVEL_LABELS: Record<string, string> = {
  pre_primary_1: 'Pre-Primary 1',
  pre_primary_2: 'Pre-Primary 2',
  grade_1: 'Grade 1',
  grade_2: 'Grade 2',
  grade_3: 'Grade 3',
  grade_4: 'Grade 4',
  grade_5: 'Grade 5',
  grade_6: 'Grade 6',
  grade_7: 'Grade 7',
  grade_8: 'Grade 8',
  grade_9: 'Grade 9',
  grade_10: 'Grade 10',
  grade_11: 'Grade 11',
  grade_12: 'Grade 12',
};

interface Teacher {
  id: number;
  full_name: string;
}

interface Assignment {
  id: number;
  school_id: number;
  class_level: string;
  teacher_id: number;
  teacher_name: string;
}

const ClassTeacherAssignment: React.FC = () => {
  const { user } = useAuth();
  const schoolId = user?.school_id;
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [assignments, setAssignments] = useState<Record<string, Assignment | null>>({});
  const [selected, setSelected] = useState<Record<string, number | ''>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});

  useEffect(() => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([
      api.get('/users/', { params: { role: 'teacher', school_id: schoolId } }),
      api.get(`/class-teachers/list/${schoolId}`)
    ]).then(([teachersRes, assignmentsRes]) => {
      setTeachers(teachersRes.data as Teacher[]);
      const assignmentMap: Record<string, Assignment | null> = {};
      
      // Initialize all class levels as null
      for (const level of CLASS_LEVELS) assignmentMap[level] = null;
      
      // Map API response to internal keys
      for (const a of assignmentsRes.data as Assignment[]) {
        // Find the internal key that matches the API class_level
        const internalKey = Object.keys(CLASS_LEVEL_LABELS).find(
          key => CLASS_LEVEL_LABELS[key] === a.class_level
        );
        if (internalKey) {
          assignmentMap[internalKey] = a;
          console.log(`Mapped ${a.class_level} to ${internalKey} with teacher ${a.teacher_name}`);
        } else {
          console.warn(`Could not map class level: ${a.class_level}`);
        }
      }
      
      setAssignments(assignmentMap);
      const sel: Record<string, number | ''> = {};
      for (const level of CLASS_LEVELS) {
        sel[level] = assignmentMap[level]?.teacher_id || '';
      }
      setSelected(sel);
    }).catch(() => {
      setSnackbar({open: true, message: 'Failed to load data', severity: 'error'});
    }).finally(() => setLoading(false));
  }, [schoolId]);

  const handleChange = (level: string, teacherId: number | '') => {
    setSelected(s => ({ ...s, [level]: teacherId }));
  };

  const handleSave = async (level: string) => {
    if (!schoolId || !selected[level]) return;
    setSaving(s => ({ ...s, [level]: true }));
    try {
      await api.post('/class-teachers/assign', {
        school_id: schoolId,
        class_level: CLASS_LEVEL_LABELS[level],
        teacher_id: selected[level],
      });
      setSnackbar({open: true, message: `Successfully assigned teacher for ${CLASS_LEVEL_LABELS[level]}`, severity: 'success'});
      setAssignments(a => ({
        ...a,
        [level]: {
          id: a[level]?.id || 0,
          school_id: schoolId,
          class_level: level,
          teacher_id: selected[level] as number,
          teacher_name: teachers.find(t => t.id === selected[level])?.full_name || '',
        }
      }));
    } catch {
      setSnackbar({open: true, message: 'Failed to assign teacher', severity: 'error'});
    } finally {
      setSaving(s => ({ ...s, [level]: false }));
    }
  };

  const getAssignmentStats = () => {
    const total = CLASS_LEVELS.length;
    const assigned = Object.values(assignments).filter(a => a !== null).length;
    const unassigned = total - assigned;
    return { total, assigned, unassigned };
  };

  const stats = getAssignmentStats();

  if (loading) {
    return (
      <Box sx={commonStyles.layout.pageContainer}>
        <Container sx={commonStyles.layout.contentContainer}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            flexDirection: 'column',
            gap: 3
          }}>
            <CircularProgress size={48} sx={{ color: colors.primary.main }} />
            <Typography variant="h6" sx={{ color: colors.text.secondary }}>
              Loading class teacher assignments...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={commonStyles.layout.pageContainer}>
      <Container sx={commonStyles.layout.contentContainer}>
        {/* Header Section */}
        <Box sx={commonStyles.layout.section}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              p: 2,
              borderRadius: '50%',
              background: colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AssignmentIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h3" sx={{ color: colors.text.primary, fontWeight: 700, mb: 1 }}>
                Class Teacher Assignment
              </Typography>
              <Typography variant="body1" sx={{ color: colors.text.secondary, fontSize: '1.1rem' }}>
                Manage teacher assignments for each class level in your school
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Box sx={commonStyles.layout.section}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={commonStyles.card}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '50%',
                    background: colors.primary.main,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <SchoolIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h4" sx={{ color: colors.primary.main, fontWeight: 700, mb: 1 }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    Total Class Levels
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={commonStyles.card}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '50%',
                    background: colors.success.main,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h4" sx={{ color: colors.success.main, fontWeight: 700, mb: 1 }}>
                    {stats.assigned}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    Assigned Teachers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={commonStyles.card}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '50%',
                    background: colors.warning.main,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <PersonIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Typography variant="h4" sx={{ color: colors.warning.main, fontWeight: 700, mb: 1 }}>
                    {stats.unassigned}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    Unassigned Classes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Assignment Table */}
        <Box sx={commonStyles.layout.section}>
          <Paper sx={commonStyles.table.container}>
            <Box sx={commonStyles.sectionHeader}>
              <Typography variant="h5" sx={{ color: colors.primary.contrastText }}>
                Class Level Assignments
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={commonStyles.table.header}>
                    <TableCell sx={{ color: colors.primary.contrastText, fontWeight: 600 }}>Class Level</TableCell>
                    <TableCell sx={{ color: colors.primary.contrastText, fontWeight: 600 }}>Current Teacher</TableCell>
                    <TableCell sx={{ color: colors.primary.contrastText, fontWeight: 600 }}>Select New Teacher</TableCell>
                    <TableCell sx={{ color: colors.primary.contrastText, fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {CLASS_LEVELS.map(level => {
                    const currentAssignment = assignments[level];
                    const isAssigned = currentAssignment !== null;
                    const hasChanges = selected[level] !== (currentAssignment?.teacher_id || '');

                    return (
                      <TableRow key={level} sx={commonStyles.table.row}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                              label={CLASS_LEVEL_LABELS[level]}
                              size="small"
                              sx={{
                                background: isAssigned ? colors.success.main : colors.warning.main,
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {currentAssignment ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{
                                width: 36,
                                height: 36,
                                bgcolor: colors.success.main,
                                fontSize: '1rem',
                                fontWeight: 600,
                                border: `2px solid ${colors.success.light}`
                              }}>
                                {currentAssignment.teacher_name[0]?.toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body1" sx={{ 
                                  color: colors.text.primary, 
                                  fontWeight: 600,
                                  fontSize: '0.95rem'
                                }}>
                                  {currentAssignment.teacher_name}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  color: colors.success.main, 
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                                  Currently Assigned
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{
                                width: 36,
                                height: 36,
                                bgcolor: colors.warning.main,
                                fontSize: '1rem',
                                fontWeight: 600,
                                border: `2px solid ${colors.warning.light}`
                              }}>
                                ?
                              </Avatar>
                              <Box>
                                <Typography variant="body1" sx={{ 
                                  color: colors.text.secondary, 
                                  fontStyle: 'italic',
                                  fontSize: '0.95rem'
                                }}>
                                  No teacher assigned
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  color: colors.warning.main, 
                                  fontWeight: 500,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <PersonIcon sx={{ fontSize: 14 }} />
                                  Assignment needed
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={selected[level]}
                            onChange={e => handleChange(level, e.target.value as number)}
                            displayEmpty
                            sx={{
                              minWidth: 200,
                              '& .MuiSelect-select': {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>Select a teacher...</em>
                            </MenuItem>
                            {teachers.map(t => (
                              <MenuItem key={t.id} value={t.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: colors.primary.main,
                                    fontSize: '0.75rem'
                                  }}>
                                    {t.full_name[0]?.toUpperCase()}
                                  </Avatar>
                                  {t.full_name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title={hasChanges ? "Save changes" : "No changes to save"}>
                              <span>
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={!hasChanges || saving[level]}
                                  onClick={() => handleSave(level)}
                                  startIcon={saving[level] ? <CircularProgress size={16} /> : <SaveIcon />}
                                  sx={getButtonStyle('primary')}
                                >
                                  {saving[level] ? 'Saving...' : 'Save'}
                                </Button>
                              </span>
                            </Tooltip>
                            {hasChanges && (
                              <Tooltip title="Cancel changes">
                                <IconButton
                                  size="small"
                                  onClick={() => handleChange(level, currentAssignment?.teacher_id || '')}
                                  sx={{
                                    color: colors.text.secondary,
                                    '&:hover': { color: colors.error.main }
                                  }}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Instructions Card */}
        <Box sx={commonStyles.layout.section}>
          <Card sx={commonStyles.card}>
            <CardContent>
              <Typography variant="h6" sx={{ color: colors.text.primary, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon sx={{ color: colors.primary.main }} />
                How to Assign Teachers
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                    <strong>1.</strong> Select a teacher from the dropdown for the desired class level
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                    <strong>2.</strong> Click the "Save" button to confirm the assignment
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 1 }}>
                    <strong>3.</strong> Use the cancel button to revert any unsaved changes
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    <strong>4.</strong> Monitor the statistics cards to track assignment progress
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({...s, open: false}))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(s => ({...s, open: false}))}
            sx={{
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: 20
              }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ClassTeacherAssignment; 